// Tabs and code groups travel as the MDX-ish tags Mintlify uses, so a body read
// as markdown (the API, the docs copy button) can be written straight back.

interface TurndownRule {
	// Narrower than a bare string so turndown's own tag-name union accepts it.
	filter: "pre" | ((node: Node) => boolean)
	replacement: (content: string, node: Node) => string
}

interface TurndownLike {
	addRule: (key: string, rule: TurndownRule) => void
}

const LANGUAGE_CLASS = /(?:^|\s)language-(\S+)/

export const escapePanelAttribute = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;")

export const indentPanelBlock = (block: string): string =>
	block
		.split("\n")
		.map((line) => (line.trim() ? `  ${line}` : line))
		.join("\n")

const hasType = (node: Node, type: string): boolean =>
	node.nodeName === "DIV" &&
	(node as HTMLElement).getAttribute("data-type") === type

export function addPanelMarkdownRules(turndown: TurndownLike): void {
	turndown.addRule("tabs", {
		filter: (node) => hasType(node, "tabs"),
		replacement: (content) =>
			`\n\n<Tabs>\n${indentPanelBlock(content.trim())}\n</Tabs>\n\n`,
	})

	turndown.addRule("tab", {
		filter: (node) => hasType(node, "tab"),
		replacement: (content, node) => {
			const element = node as HTMLElement
			const title = element.getAttribute("data-title") ?? "Tab"
			return `\n<Tab title="${escapePanelAttribute(
				title,
			)}">\n${indentPanelBlock(content.trim())}\n</Tab>\n`
		},
	})

	turndown.addRule("cardGroup", {
		filter: (node) => hasType(node, "card-group"),
		replacement: (content, node) => {
			const cols = (node as HTMLElement).getAttribute("data-cols") ?? "2"
			return `\n\n<CardGroup cols={${cols}}>\n${indentPanelBlock(content.trim())}\n</CardGroup>\n\n`
		},
	})

	// Mintlify's card carries its title and link as attributes, not as a line.
	turndown.addRule("card", {
		filter: (node) => hasType(node, "card"),
		replacement: (content, node) => {
			const title = (node as HTMLElement).querySelector("[data-card-title]")
			const text = title?.textContent?.trim() ?? ""
			const href = title?.querySelector("a")?.getAttribute("href")
			const icon = title
				?.querySelector('[data-type="icon"]')
				?.getAttribute("data-icon")
			const attributes = [
				`title="${escapePanelAttribute(text)}"`,
				icon ? `icon="${escapePanelAttribute(icon)}"` : "",
				href ? `href="${escapePanelAttribute(href)}"` : "",
			]
				.filter(Boolean)
				.join(" ")
			return `\n<Card ${attributes}>\n${indentPanelBlock(content.trim())}\n</Card>\n`
		},
	})

	turndown.addRule("cardTitle", {
		filter: (node) =>
			node.nodeName === "P" &&
			(node as HTMLElement).hasAttribute("data-card-title"),
		replacement: () => "",
	})

	turndown.addRule("codeGroup", {
		filter: (node) => hasType(node, "code-group"),
		replacement: (content) =>
			`\n\n<CodeGroup>\n${content.trim()}\n</CodeGroup>\n\n`,
	})

	// Our code blocks carry a copy button ahead of the <code>, which turndown's
	// own fenced rule skips, and the fence has to keep the group's label.
	turndown.addRule("codeBlock", {
		filter: "pre",
		replacement: (_content, node) => {
			const element = node as HTMLElement
			const code = element.querySelector("code")
			const text = code?.textContent ?? element.textContent ?? ""
			const language =
				element.getAttribute("data-language") ??
				LANGUAGE_CLASS.exec(
					`${code?.getAttribute("class") ?? ""} ${element.getAttribute("class") ?? ""}`,
				)?.[1] ??
				null
			const info = [
				language === "plaintext" ? "" : language,
				element.getAttribute("data-title"),
			]
				.filter(Boolean)
				.join(" ")
			return `\n\n\`\`\`${info}\n${text.replace(/\n$/, "")}\n\`\`\`\n\n`
		},
	})
}
